import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReseedDrillsResponse } from '../Model';
import { restClient } from '../RestClient';
import { AuthService } from '../AuthService';
import { LoadingButton } from '../LoadingButton';
import { useReferenceData } from '../ReferenceDataContext';

interface ReseedDrillsProps {
  onError?: (message: string) => void;
}

const MAX_VIDEOS_OPTIONS = [10, 20, 30];

export const ReseedDrills: React.FC<ReseedDrillsProps> = ({ onError }) => {
  const navigate = useNavigate();
  const [deleteExisting, setDeleteExisting] = useState(true);
  const [maxVideosPerSearch, setMaxVideosPerSearch] = useState(20);
  const [isReseeding, setIsReseeding] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [result, setResult] = useState<ReseedDrillsResponse | null>(null);

  const { currentSport } = useReferenceData();
  const isSuperUser = AuthService.isSuperUser();

  useEffect(() => {
    if (!isSuperUser) {
      navigate('/training-drills');
    }
  }, [isSuperUser, navigate]);

  const handleStartReseed = () => {
    setShowConfirmation(true);
  };

  const handleConfirmReseed = async () => {
    setShowConfirmation(false);
    setIsReseeding(true);
    setResult(null);

    try {
      const response = await restClient.reseedDrills({
        deleteExistingDrills: deleteExisting,
        maxVideosPerSearch,
        sport: currentSport,
      });

      if (response.IsSuccess) {
        setResult(response.Value);
        if (!response.Value.success && response.Value.errors.length > 0) {
          onError?.(response.Value.errors.join(', '));
        }
      } else {
        onError?.(response.Error?.description ?? 'Failed to reseed drills');
      }
    } catch (error) {
      onError?.('An unexpected error occurred while reseeding drills');
    } finally {
      setIsReseeding(false);
    }
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  return (
    <div className="reseed-drills-page">
      {/* Header */}
      <div className="youtube-search-header">
        <button onClick={() => navigate('/training-drills')} className="btn btn-secondary">
          Back to Drills
        </button>
        <h1 className="youtube-search-header-title">Reseed Drills Database</h1>
      </div>

      {/* Warning */}
      <div className="alert alert-warning mb-4">
        <strong>Warning:</strong> This operation will search YouTube for {currentSport.toLowerCase()} training drills across
        multiple skill/category/difficulty combinations and populate the drills database.
        {deleteExisting && ` All existing ${currentSport.toLowerCase()} drills will be deleted first.`}
        This process may take several minutes to complete.
      </div>

      {/* Configuration */}
      <div className="card mb-4">
        <div className="card-body">
          <h3 className="text-lg font-semibold mb-4">Configuration</h3>

          <div className="form-group mb-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteExisting}
                onChange={(e) => setDeleteExisting(e.target.checked)}
                className="form-checkbox"
                disabled={isReseeding}
              />
              <span>Delete existing drills before reseeding</span>
            </label>
            <p className="text-muted text-sm mt-1">
              If unchecked, new drills will be added alongside existing ones (duplicates may occur).
            </p>
          </div>

          <div className="form-group mb-4">
            <label className="form-label">Max videos per search</label>
            <select
              value={maxVideosPerSearch}
              onChange={(e) => setMaxVideosPerSearch(Number(e.target.value))}
              className="form-select"
              style={{ maxWidth: '200px' }}
              disabled={isReseeding}
            >
              {MAX_VIDEOS_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} videos
                </option>
              ))}
            </select>
            <p className="text-muted text-sm mt-1">
              Higher values will take longer but may find more diverse drills.
              Approximately 45 searches will be performed.
            </p>
          </div>

          <LoadingButton
            onClick={handleStartReseed}
            isLoading={isReseeding}
            loadingText="Reseeding..."
            className="btn btn-danger"
            disabled={isReseeding}
          >
            Start Reseed
          </LoadingButton>
        </div>
      </div>

      {/* Progress indicator */}
      {isReseeding && (
        <div className="card mb-4">
          <div className="card-body">
            <div className="youtube-loading">
              <div className="youtube-spinner" />
              <p>Searching YouTube and creating drills...</p>
              <p className="text-muted text-sm">This may take several minutes. Please do not close this page.</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="card mb-4">
          <div className="card-body">
            <h3 className="text-lg font-semibold mb-4">
              {result.success ? 'Reseed Complete' : 'Reseed Completed with Errors'}
            </h3>

            <div className="reseed-results-grid">
              <div className="reseed-result-item">
                <span className="reseed-result-label">Drills Created</span>
                <span className="reseed-result-value text-success">{result.drillsCreated}</span>
              </div>
              <div className="reseed-result-item">
                <span className="reseed-result-label">Drills Deleted</span>
                <span className="reseed-result-value">{result.drillsDeleted}</span>
              </div>
              <div className="reseed-result-item">
                <span className="reseed-result-label">Searches Performed</span>
                <span className="reseed-result-value">{result.searchesPerformed}</span>
              </div>
              <div className="reseed-result-item">
                <span className="reseed-result-label">Duplicates Filtered</span>
                <span className="reseed-result-value">{result.duplicatesFiltered}</span>
              </div>
              <div className="reseed-result-item">
                <span className="reseed-result-label">Duration</span>
                <span className="reseed-result-value">{result.durationSeconds.toFixed(1)}s</span>
              </div>
            </div>

            {result.errors.length > 0 && (
              <div className="alert alert-error mt-4">
                <strong>Errors:</strong>
                <ul className="mt-2">
                  {result.errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4">
              <button
                onClick={() => navigate('/training-drills')}
                className="btn btn-primary"
              >
                View Drills
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2 className="modal-title">Confirm Reseed Operation</h2>
            <p className="mb-4">
              Are you sure you want to reseed the {currentSport.toLowerCase()} drills database?
              {deleteExisting && (
                <strong className="text-danger"> All existing {currentSport.toLowerCase()} drills will be permanently deleted.</strong>
              )}
            </p>
            <p className="text-muted mb-4">
              This operation will search YouTube for {currentSport.toLowerCase()} training drills and may take several minutes.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleCancelConfirmation}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReseed}
                className="btn btn-danger"
              >
                Confirm Reseed
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
